import type { Activity, MessageActivityDetails, NoteActivityDetails, Owner } from '@paintscout/api'
import React, { useState } from 'react'
import { makeStyles, useTheme } from '@material-ui/core'
import AccountCircleIcon from '@material-ui/icons/AccountCircle'
import { getActivityVariant, getActivityColor, removeAuth0Prefix, convertToOwner } from '@paintscout/util'
import classnames from 'classnames'
import getActivityTooltip from '@paintscout/util/util/getActivityTooltip/getActivityTooltip'
import { formatName, startCase, getCrews } from '@paintscout/util/builder'
import Tooltip from '@paintscout/ui/components/Tooltip'
import Typography from '@paintscout/ui/components/Typography'
import Avatar from '@paintscout/ui/components/Avatar/Avatar'
import { LightningIcon } from '@paintscout/ui/icons'
import { useClientOptions } from '../../providers/ClientOptionsProvider'
import { useUsers } from '../../providers/UsersProvider'
import { withLiveOwnerName } from '../../util/withLiveOwnerName'

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative'
  },
  img: {
    marginRight: '10px',
    borderRadius: '50%',
    objectFit: 'cover',
    height: '28px',
    width: '28px',
    zIndex: 2
  },
  primaryCustomer: {
    '& path': {
      transform: 'scale(1.15)',
      transformOrigin: 'center'
    }
  },
  lightningIcon: {
    top: -4,
    left: 16,
    position: 'absolute',
    width: '18px',
    height: '18px',
    zIndex: 3
  },
  customerIcon: {
    backgroundColor: theme.palette.common.white,
    borderRadius: '50%',
    color: theme.paintscout.palette.brand.Neutral.P35,
    outline: 'none !important',
    width: '20px !important',
    height: '20px !important',
    '& path': {
      transform: 'scale(1.2)',
      transformOrigin: 'center'
    }
  },
  primaryUserPicture: {
    borderRadius: '50%',
    border: `2px solid ${theme.palette.common.white}`,
    backgroundColor: theme.palette.common.white,
    objectFit: 'cover',

    width: '20px',
    height: '20px',
    position: 'relative',
    marginRight: '19px !important',
    marginTop: '5px !important',
    marginLeft: '-1px !important',
    zIndex: 2
  },
  secondaryUserPicture: {
    borderRadius: '50%',
    border: `2px solid ${theme.palette.common.white}`,
    backgroundColor: theme.palette.common.white,
    objectFit: 'cover',

    width: '20px',
    height: '20px',
    position: 'absolute',
    top: -1,
    left: 10,
    zIndex: 1
  },
  secondaryAvatar: {
    top: 1,
    left: 12
  },

  primaryUserAvatar: {
    borderRadius: '50%',
    backgroundColor: theme.palette.common.white,
    objectFit: 'cover',
    overflow: 'hidden',
    position: 'relative',
    marginRight: '21px !important',
    marginTop: '7px !important',
    marginLeft: '1px !important',
    zIndex: 2,
    border: `2px solid ${theme.palette.common.white}`
  },
  avatar: {
    marginRight: '12px',
    marginLeft: '2px',
    marginTop: '2px'
  }
}))

interface ActivityAvatarProps {
  activity: Activity
}

const OTHER_USER_VARIANT = 'other-user'
const DEAL_STAGE_CHANGED = 'deal-stage-changed'
const CUSTOMER_VARIANTS = ['customer', OTHER_USER_VARIANT] as const
const EVENT_CREATED_ASSIGNED = ['event-created', 'event-assigned']
const AUTOMATED_CUSTOMER_VARIANT = 'automated-customer'
const AUTOMATION_VARIANTS = ['automation', AUTOMATED_CUSTOMER_VARIANT]

export default function ActivityAvatar({ activity }: ActivityAvatarProps) {
  const classes = useStyles()
  const { options } = useClientOptions()
  const { users, usersById } = useUsers()

  const variant = getActivityVariant(activity, options)

  // Avatar URL denormalized onto the message author server-side (customer-view,
  // where useUsers() is empty). Falls back to the useUsers lookup when absent.
  const messageAvatar =
    activity.details?.activityType === 'message'
      ? (activity.details as MessageActivityDetails).message?.from?.avatar ?? undefined
      : undefined

  const getEntityOwner = (activity: Activity): Owner => {
    if (activity.details?.activityType === 'note') {
      return activity.details?.owner as Owner
    }
    if ((activity.details as any)?.crew) {
      const crewOptions = getCrews({ options })
      const crew = crewOptions.find((crew) => crew._id === (activity.details as any)?.crew)
      if (crew) {
        return {
          ...crew.projectManager,
          id: removeAuth0Prefix(crew.projectManager.id)
        }
      }
    }
    return convertToOwner({ owner: activity.details?.quoteOwner }) ?? activity.details?.owner
  }

  const getActivityOwner = (activity: Activity): Owner => {
    if (activity.details?.activityType === 'note') {
      const versions = (activity.details as NoteActivityDetails).versions ?? []
      if (versions.length > 0) {
        const latestVersion = versions[0]
        return latestVersion?.owner ?? activity.details?.owner
      }
    }
    return activity.details?.owner ?? convertToOwner({ owner: activity.details?.quoteOwner })
  }

  // Overlay the live name onto both owner snapshots once, here, so every name downstream — the
  // avatar initials, the tooltip, and getActivityTooltip's subtitle — is immune to a seat rename
  // the fan-out missed (#21800). Ids and every other field are untouched.
  const entityOwner = withLiveOwnerName(getEntityOwner(activity), usersById)
  const activityOwner = withLiveOwnerName(getActivityOwner(activity), usersById)

  // Default to active if we don't get any estimator info for the owner
  const entityOwnerActive =
    users.find((user) => removeAuth0Prefix(user.user_id) === removeAuth0Prefix(entityOwner?.id))?.app_metadata
      ?.active ?? true
  const activityOwnerActive =
    users.find((user) => removeAuth0Prefix(user.user_id) === removeAuth0Prefix(activityOwner?.id))?.app_metadata
      ?.active ?? true

  const hoverSubtitle =
    CUSTOMER_VARIANTS.includes(variant as (typeof CUSTOMER_VARIANTS)[number]) ||
    activity.details?.activityType === DEAL_STAGE_CHANGED
      ? getActivityTooltip(
          activity,
          EVENT_CREATED_ASSIGNED.includes(activity.details?.activityType) ? entityOwner : activityOwner,
          variant as 'customer' | 'other-user',
          options
        )
      : null

  const getTooltipOwner = () => {
    if (variant === 'automation' && activity.details?.activityType !== DEAL_STAGE_CHANGED) {
      return 'Automatically sent for '
    }
    if (activity.details?.activityType === 'note') {
      return 'Created by '
    }
    if (activity.details?.activityType === 'event-created') {
      return 'Scheduled by '
    }
    if (
      [
        'event-assigned',
        'event-cancelled',
        'event-progress-updated',
        'task-created',
        'task-assigned',
        'task-completed',
        'deal-assigned'
      ].includes(activity.details?.activityType)
    ) {
      return 'Assigned to '
    }
    if (['deal', 'contact', 'company'].includes(activity.details?.docType)) {
      return `${startCase(activity.details?.docType)} owner: `
    }
    return 'Estimator: '
  }

  const getMainAvatar = () => {
    if (variant === 'customer' || (variant === 'automation' && activity.details?.activityType === DEAL_STAGE_CHANGED)) {
      return entityOwner ?? activityOwner
    }
    return activityOwner ?? entityOwner
  }

  const tooltipUserName = EVENT_CREATED_ASSIGNED.includes(activity.details?.activityType)
    ? formatName(activityOwner)
    : formatName(entityOwner)

  const tooltipUserActive = EVENT_CREATED_ASSIGNED.includes(activity.details?.activityType)
    ? activityOwnerActive
    : entityOwnerActive

  return (
    <Tooltip
      className={classes.root}
      hideIcon
      disabled={!tooltipUserName && !hoverSubtitle}
      content={
        <>
          {hoverSubtitle && (
            <Typography variant="body2" align="center" weight={500}>
              {`${hoverSubtitle}${!activityOwnerActive ? ' (deactivated)' : ''}`}
            </Typography>
          )}
          {tooltipUserName && (
            <Typography
              variant="body2"
              align="center"
              weight={hoverSubtitle ? 400 : 500}
              style={{ marginTop: hoverSubtitle ? '4px' : '0px' }}
            >
              {`${hoverSubtitle || variant === 'automation' ? getTooltipOwner() : ''}${tooltipUserName} ${!tooltipUserActive ? ' (deactivated)' : ''}`}
            </Typography>
          )}
        </>
      }
    >
      <>
        {variant !== AUTOMATED_CUSTOMER_VARIANT && (
          <UserPictureWrapper
            owner={getMainAvatar()}
            avatar={messageAvatar}
            variant={variant === OTHER_USER_VARIANT ? 'primary' : variant === 'customer' ? 'secondary' : 'standard'}
          />
        )}
        {variant === AUTOMATED_CUSTOMER_VARIANT && (
          <AccountCircleIcon className={classnames(classes.img, classes.primaryCustomer)} />
        )}
        {variant === 'customer' && (
          <AccountCircleIcon className={classnames(classes.primaryUserPicture, classes.customerIcon, 'no-redirect')} />
        )}
        {AUTOMATION_VARIANTS.includes(variant) && <LightningIcon className={classes.lightningIcon} />}
        {variant === OTHER_USER_VARIANT && <UserPictureWrapper owner={entityOwner} variant="secondary" />}
      </>
    </Tooltip>
  )
}

const UserPictureWrapper = ({
  owner,
  avatar,
  variant = 'standard'
}: {
  owner?: Owner
  avatar?: string
  variant?: 'standard' | 'primary' | 'secondary'
}) => {
  const { users } = useUsers()
  const user = users.find((user) => removeAuth0Prefix(user.user_id) === removeAuth0Prefix(owner?.id))
  // owner.avatar is denormalized server-side for customer-view message queries
  // (where useUsers() is empty); null elsewhere
  const userPicture = avatar ?? owner?.avatar ?? (owner?.id ? user?.user_metadata?.customAvatar?.value : null)
  const userColor = getActivityColor(user?.user_metadata?.color)
  // `owner` already carries the live name — ActivityAvatar resolves it before passing it down.
  const userName = owner?.id ? formatName(owner) : ''

  const active = user?.app_metadata?.active ?? true

  return (
    <AvatarDisplay
      userPicture={userPicture}
      userName={userName}
      userColor={userColor}
      active={active}
      variant={variant}
    />
  )
}

export function AvatarDisplay({
  userPicture,
  userName,
  userColor,
  active = true,
  variant = 'standard'
}: {
  userPicture?: string
  userName?: string
  userColor?: string
  active?: boolean
  variant?: 'standard' | 'primary' | 'secondary'
}) {
  const classes = useStyles()
  const [imgLoading, setImgLoading] = useState(true)
  const theme = useTheme()
  if (userPicture) {
    return (
      <img
        className={classnames({
          'no-redirect': true,
          [classes.img]: variant === 'standard',
          [classes.primaryUserPicture]: variant === 'primary',
          [classes.secondaryUserPicture]: variant === 'secondary'
        })}
        src={userPicture}
        style={{
          opacity: imgLoading ? 0 : 1,
          transition: 'opacity 0.2s ease-in',
          filter: !active ? 'contrast(50%) brightness(1.4)' : undefined
        }}
        alt={userName}
        onLoad={() => {
          setImgLoading(false)
        }}
      />
    )
  }

  const shrink = ['primary', 'secondary'].includes(variant)

  const baseStyle = {
    paddingTop: '1px',
    paddingLeft: '1px',
    fontSize: shrink ? '7px' : '10px',
    cursor: 'default'
  }

  const inactiveStyle = { color: theme.paintscout.palette.brand.Neutral.P70 }

  return (
    <Avatar
      className={classnames({
        'no-redirect': true,
        [classes.avatar]: variant === 'standard',
        [classes.primaryUserAvatar]: variant === 'primary',
        [classes.secondaryUserPicture]: variant === 'secondary',
        [classes.secondaryAvatar]: variant === 'secondary'
      })}
      width={shrink ? '16px' : '24px'}
      height={shrink ? '16px' : '24px'}
      backgroundColor="#FFFFFF"
      disableTooltip
      filled
      avatarColor={active ? userColor : theme.paintscout.palette.brand.Neutral.P94}
      style={{ ...baseStyle, ...(active ? {} : inactiveStyle) }}
    >
      {/* Limit the username to two words so we only get two initials */}
      {userName.toUpperCase().split(/\s+/).slice(0, 2).join(' ')}
    </Avatar>
  )
}
